import _ from 'lodash';
import { useEffect, useMemo } from 'react';
import { KEY_RECOIL } from '@/shared/config';
import {
  DEFAULT_CONTROL_MODAL_VALUE,
  selectControlModal,
  selectEventChanges,
  selectSelectedColumnInfo,
  selectSelectedEventIndex,
  selectSelectedMultipleStrip,
  selectSelectedStrip,
  useReportStore,
} from '@/entities/report';

/**
 * Supplies the rhythm events tab's modal layer with the current selection, report metadata and a cancel handler per modal. It merges the selected strip with any pending event change and its original data, resolves the selected event from the current column info, reads study, profile, timezone and report window from the report store, and resets the control modal whenever the selected event changes.
 *
 * @returns An object carrying the tab key, control modal state, selection, report metadata and the per-modal cancel handlers.
 */
const useRhythmEventsModal = () => {
  const keyRecoil = KEY_RECOIL.RHYTHM_EVENT;

  const controlModal = useReportStore(selectControlModal(keyRecoil));
  const setControlModalAction = useReportStore((s) => s.actions.setControlModal);
  /**
   * Writes the rhythm events tab's control modal state. Passes the TAB_2 key with the new control modal state or updater to the store's setControlModal action.
   *
   * @param v - The next control modal state, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setControlModal = (v: Parameters<typeof setControlModalAction>[1]) =>
    setControlModalAction(keyRecoil, v);
  const resetControlModalAction = useReportStore((s) => s.actions.resetControlModal);
  /**
   * Clears the rhythm events tab's control modal state back to its defaults. Forwards the TAB_2 key to the store's resetControlModal action.
   *
   * @returns Nothing.
   */
  const resetControlModal = () => resetControlModalAction(keyRecoil);
  const selected = useReportStore(selectSelectedStrip(keyRecoil));
  const selectedMultipleStrip = useReportStore(selectSelectedMultipleStrip(keyRecoil));
  const selectedColumnInfo = useReportStore(selectSelectedColumnInfo(keyRecoil));
  const selectedEventIndex = useReportStore(selectSelectedEventIndex(keyRecoil));
  const eventChanges = useReportStore(selectEventChanges(keyRecoil));

  const selectedEvent = _.isEmpty(selectedColumnInfo)
    ? null
    : selectedColumnInfo[selectedEventIndex];

  const selectedStrip = useMemo(() => {
    // selectedStripState (Recoil, Task 7) is typed IEvent | IBeat | IEcgBookmark, missing idEvent/originalData on some members - atom-type gap.
    const selectedAny = selected as any;
    const foundEventChange = eventChanges.find((x: any) => x.idEvent === selectedAny?.idEvent);
    if (foundEventChange) {
      return foundEventChange;
    }
    return { ...(selectedAny || {}), ...(selectedAny?.originalData || {}) };
  }, [selected, eventChanges]);
  // reportInfoState (Recoil, Task 7) is typed {} | IReportInfo - cast to destructure.
  const { studyId, profileId, timezone, startReport, timezoneOffset, stopReport } = useReportStore(
    (s) => s.reportInfo,
  ) as any;
  const ecgDataMap = useReportStore((s) => s.ecgDataMap);

  /**
   * Closes the add-new-event modal of the rhythm events tab, optionally handing over to the new-events list. Resets the control modal to its defaults with isShowAddNewEvent off and isShowNewEvents taken from the argument.
   *
   * @param isOpenNewEvent - Whether the new-events modal should open as the add-new-event modal closes.
   * @returns Nothing.
   */
  const onClickCancelAddNewEvent = (isOpenNewEvent = false) => {
    setControlModal({
      ...DEFAULT_CONTROL_MODAL_VALUE,
      isShowAddNewEvent: false,
      isShowNewEvents: isOpenNewEvent,
    });
  };

  /**
   * Closes the new-events modal of the rhythm events tab. Resets the control modal to its defaults with isShowNewEvents off.
   *
   * @returns Nothing.
   */
  const onClickCancelNewEvents = () => {
    setControlModal({ ...DEFAULT_CONTROL_MODAL_VALUE, isShowNewEvents: false });
  };

  /**
   * Closes the bulk event-type change modal of the rhythm events tab. Resets the control modal to its defaults with isShowChangeTypeBulk off.
   *
   * @returns Nothing.
   */
  const onClickCancelChangeEventTypeBulk = () => {
    setControlModal({ ...DEFAULT_CONTROL_MODAL_VALUE, isShowChangeTypeBulk: false });
  };

  /**
   * Closes the button-type event duration modal of the rhythm events tab. Resets the control modal to its defaults with isShowChangeDurationButtonType off.
   *
   * @returns Nothing.
   */
  const onClickCancelChangeEventDurationButtonType = () => {
    setControlModal({ ...DEFAULT_CONTROL_MODAL_VALUE, isShowChangeDurationButtonType: false });
  };

  /**
   * Closes the selected-info event duration modal of the rhythm events tab. Resets the control modal to its defaults with isShowChangeDurationSelectedInfo off.
   *
   * @returns Nothing.
   */
  const onClickCancelChangeEventDurationSelectedInfo = () => {
    setControlModal({ ...DEFAULT_CONTROL_MODAL_VALUE, isShowChangeDurationSelectedInfo: false });
  };

  /**
   * Closes the single event-type change modal of the rhythm events tab. Resets the control modal to its defaults with isShowChangeTypeSingle off.
   *
   * @returns Nothing.
   */
  const onClickCancelChangeEventTypeSingle = () => {
    setControlModal({ ...DEFAULT_CONTROL_MODAL_VALUE, isShowChangeTypeSingle: false });
  };

  /**
   * Closes the included-strips modal of the rhythm events tab. Resets the control modal to its defaults with isShowIncludedStrips off.
   *
   * @returns Nothing.
   */
  const onClickCancelIncludedStrips = () => {
    setControlModal({ ...DEFAULT_CONTROL_MODAL_VALUE, isShowIncludedStrips: false });
  };

  useEffect(() => {
    if (controlModal.isShowChangeDurationSelectedInfo) {
      resetControlModal();
    }
  }, [selectedEvent?.id]);

  return {
    keyRecoil,
    controlModal,
    selectedStrip,
    selectedMultipleStrip,
    selectedEvent,
    studyId,
    profileId,
    timezone,
    startReport,
    timezoneOffset,
    stopReport,
    ecgDataMap,
    onClickCancelAddNewEvent,
    onClickCancelNewEvents,
    onClickCancelChangeEventTypeBulk,
    onClickCancelChangeEventDurationButtonType,
    onClickCancelChangeEventDurationSelectedInfo,
    onClickCancelChangeEventTypeSingle,
    onClickCancelIncludedStrips,
  };
};

export default useRhythmEventsModal;
